import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { Dialog, DialogHandle, Modal } from 'components/modal_manager';
import { useOrderHistory } from 'pages/orders/order_history/context';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Alert, Button, ListGroup } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { Order, OrderApi } from 'apis/OrderApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';
type OrderDetailsDialogShowOptions = {
    order: Order
}

export type OrderDetailsDialogHandle = {
    hide: () => void;
    show: (options: OrderDetailsDialogShowOptions) => void;
    visible: boolean;
};

export const OrderDetailsDialog = forwardRef<OrderDetailsDialogHandle>((_props, forwardedRef) => {

    const [state, setState, resetState] = useSuperState({
        comment: '',
        error: null as string,
        loading: false,
        order: null as Order,
        purchaseOrder: '',
        visible: false
    });
    const { comment, error, loading, order, purchaseOrder, visible } = state;

    const { highestRole } = useApp();
    const { formatMessage } = useIntl();
    const { fetchOrders } = useOrderHistory();

    const isSuperAdmin = highestRole.id === 1;
    const canEditPO = highestRole.id < 3;

    const dialogRef = useRef<DialogHandle>(null);
    const intl = useIntl();

    const clearError = () => setState({ error: null });

    const show = (options: OrderDetailsDialogShowOptions) => {

        const { order = null } = options;

        setState({
            order,
            purchaseOrder: order?.PurchaseOrder ?? '',
            visible: true
        });

    };

    const hide = () => resetState();

    const handleSave = async () => {

        const { comment, order, purchaseOrder } = state;
        if (!order) return;

        try {

            setState({ loading: true });

            if (canEditPO && purchaseOrder !== (order.PurchaseOrder ?? '')) {

                await OrderApi.UpdatePurchaseOrder(order.Id ?? order.ID, purchaseOrder);

            }

            if (isSuperAdmin && comment.trim()) {

                await OrderApi.UpdateComment(order.Id ?? order.ID, comment);

            }

            hide();

        } catch (e: any) {

            setState({
                error: e?.Message || e?.message || formatMessage({ id: 'failed_to_save_order.60dffb79', defaultMessage: 'Failed to save order.' }),
                loading: false
            });

        }

    };

    const handleRetract = async () => {

        const { order } = state;
        if (!order) return;

        try {

            setState({ loading: true });

            await OrderApi.RetractOrder(order.Id ?? order.ID);

            hide();
            fetchOrders();

        } catch (e: any) {

            setState({
                error: e?.Message || e?.message || formatMessage({ id: 'failed_to_retract_order.6c20a95d', defaultMessage: 'Failed to retract order.' }),
                loading: false
            });

        }

    };

    const renderDate = (dateStr: string) => dateStr
        ? intl.formatDate(new Date(dateStr), { day: '2-digit', month: '2-digit', year: 'numeric' })
        : 'N/A';

    const handleRetractConfirmation = () => {

        const dialog = dialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction(),
                dialog.OKAction({
                    label: formatMessage({ id: 'retract.59679f7f', defaultMessage: 'Retract' }),
                    onClick: () => handleRetract(),
                    variant: 'danger'
                })
            ],
            body: (
                <p>
                    <FormattedMessage defaultMessage="Are you sure you would like to return this order into your inventory?" id="are_you_sure_you_would_like_to_return_th.4893a0b4" />
                </p>
            ),
            title: formatMessage({ id: 'confirm_retract.ced8e371', defaultMessage: 'Confirm Retract' })
        });

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {

        if (!visible || !state?.order) return;

        setState({
            comment: '',
            loading: false,
            purchaseOrder: state?.order?.PurchaseOrder ?? ''
        });

    }, [state?.visible, state?.order]);

    useImperativeHandle(forwardedRef, (): OrderDetailsDialogHandle => ({
        hide,
        show,
        visible
    }));

    return (
        <Modal className={'order-details-dialog'} onHide={hide} show={visible} size={'lg'}>

            <Modal.Header closeButton>
                <Modal.Title>
                    {<FormattedMessage defaultMessage="Order Details" id="order_details.f1808d77" />}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                    {error}
                </Alert>

                <ListGroup>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="License" description="Formulario" id="license.2240261c" />}
                        </strong>
                        <span>
                            {order?.License?.Name}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="License Type" description="Formulario" id="license_type.3286e892" />}
                        </strong>
                        <span>
                            {order?.License?.LicenseTypeString}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Term" description="Formulario" id="term.03dbdea8" />}
                        </strong>
                        <span>
                            {order?.License?.UsageDays}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Requested" description="Gridview header" id="requested.f99eee8e" />}
                        </strong>
                        <span>
                            {order?.NumRequested}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Remaining" description="Gridview header" id="remaining.a4b6ea18" />}
                        </strong>
                        <span>
                            {order?.NumAvailable}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Order ID" description="Formulario" id="order_id.ab576b3d" />}
                        </strong>
                        <span>
                            {order?.OrderControl}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Status" id="status.ad856ad7" />}
                        </strong>
                        <span>
                            {order?.OrderStatusText}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Creation Date" description="Formulario" id="creation_date.48db08f0" />}
                        </strong>
                        <span>
                            {renderDate(order?.DateCreate)}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Start Date" id="start_date.66ba5b72" />}
                        </strong>
                        <span>
                            {renderDate(order?.DateStart)}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Partner" id="partner.cb022868" />}
                        </strong>
                        <span>
                            {order?.Partner?.Name}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Ordered By" description="Formulario" id="ordered_by.381902ec" />}
                        </strong>
                        <span>
                            {order?.OrderedBy}
                        </span>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <strong>
                            {<FormattedMessage defaultMessage="Renewed By" id="renewed_by.ab5b1f75" />}
                        </strong>
                        <span>
                            {order?.RenewedControl || 'N/A'}
                        </span>
                    </ListGroup.Item>

                </ListGroup>

                {canEditPO && (
                    <div className="mt-3">
                        <GMetrixInput
                            label={formatMessage({ id: 'purchase_order.17844493', defaultMessage: 'Purchase Order' })}
                            labelClass="fw-bold"
                            maxLength={50}
                            onChange={e => setState({ purchaseOrder: e.target.value })}
                            value={purchaseOrder}
                        />
                    </div>
                )}

                {isSuperAdmin && (
                    <div className="mt-3">
                        <label className="fw-bold">
                            {<FormattedMessage defaultMessage="Add Comment" id="add_comment.929f430f" />}
                            :
                        </label>
                        <textarea
                            className="form-control mt-1"
                            cols={45}
                            onChange={e => setState({ comment: e.target.value })}
                            rows={2}
                            value={comment}
                        />
                        {order?.Comment && (
                            <div className="mt-2 small d-flex flex-column gap-1">
                                <label className={'fw-bold'}>
                                    {<FormattedMessage defaultMessage="Past Comments" id="past_comments.cf923217" />}
                                    :
                                </label>

                                <em className={'p-1 rounded bg-body-tertiary'}>
                                    {order.Comment}
                                </em>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>

            {(canEditPO || isSuperAdmin || order?.AllowRetract) && (
                <Modal.Footer className={'d-flex justify-content-between'}>

                    <div>
                        {order?.AllowRetract && (
                            <Button
                                disabled={loading}
                                onClick={handleRetractConfirmation}
                                variant="outline-danger"
                            >
                                <FormattedMessage defaultMessage="Retract Order" id="retract_order.c8f1bea1" />
                            </Button>
                        )}
                    </div>

                    {(canEditPO || isSuperAdmin) && (
                        <div className={'d-flex align-items-center gap-1'}>

                            <Button onClick={hide} variant={'outline-secondary'}>
                                {<FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />}
                            </Button>

                            <Button onClick={handleSave}>
                                {<FormattedMessage defaultMessage="Save" id="save.d44082ae" />}
                            </Button>

                        </div>
                    )}

                </Modal.Footer>
            )}

            <Dialog ref={dialogRef} />

        </Modal>
    );

});

OrderDetailsDialog.displayName = 'OrderDetailsDialog';
